import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
// hl-custom: variant hover preview — import + constant for previewMedia below
import { debounce } from '@theme/utilities';

const PREVIEW_ATTRIBUTES = ['src', 'srcset', 'style', 'alt'];

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  // hl-custom(start): variant hover preview — called by assets/variant-picker.js when a
  // variant option in snippets/variant-main-picker.liquid is hovered; swaps the first
  // gallery image and restores it on hover-out.
  /** @type {Map<HTMLImageElement, Record<string, string | null>>} */
  #previewedImages = new Map();

  /**
   * Temporarily shows the given media in the gallery's first position
   * (both carousel and grid trees), e.g. while hovering a variant option.
   * The media's own gallery element is the preferred source; the fallback
   * covers galleries with hide_variants enabled, where unselected variants'
   * media isn't rendered in the DOM.
   * @param {number | string} id - The media id to preview.
   * @param {{url?: string, srcset?: string}} [fallback] - Image URLs to use when the media isn't in the gallery.
   */
  previewMedia(id, fallback) {
    this.resetMediaPreview.cancel();
    this.#restorePreviewedImages();

    const source = this.querySelector(`.product-media[data-media-id="${id}"] .product-media__image`);

    /** @type {Record<string, string | null> | null} */
    let next = null;
    if (source instanceof HTMLImageElement) {
      next = {};
      for (const name of PREVIEW_ATTRIBUTES) next[name] = source.getAttribute(name);
    } else if (fallback?.url) {
      next = { src: fallback.url, srcset: fallback.srcset ?? null };
    }
    if (!next) return;

    for (const target of this.#firstMediaImages()) {
      if (target === source) continue;

      const original = {};
      for (const [name, value] of Object.entries(next)) {
        original[name] = target.getAttribute(name);
        if (value === null) {
          target.removeAttribute(name);
        } else {
          target.setAttribute(name, value);
        }
      }
      this.#previewedImages.set(target, original);
    }
  }

  /**
   * Restores the first media after a hover preview. Debounced so quick
   * moves between options don't flash the original image in between.
   */
  resetMediaPreview = debounce(() => this.#restorePreviewedImages(), 100);

  #restorePreviewedImages() {
    for (const [image, original] of this.#previewedImages) {
      for (const [name, value] of Object.entries(original)) {
        if (value === null) {
          image.removeAttribute(name);
        } else {
          image.setAttribute(name, value);
        }
      }
    }
    this.#previewedImages.clear();
  }

  #firstMediaImages() {
    const images = [];
    const slideImage = this.refs.slideshow?.querySelector('.product-media__image');
    if (slideImage instanceof HTMLImageElement) images.push(slideImage);
    const gridImage = this.refs.media?.[0]?.querySelector('.product-media__image');
    if (gridImage instanceof HTMLImageElement) images.push(gridImage);
    return images;
  }
  // hl-custom(end): variant hover preview

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
